import { useMemo } from "react";

import { readPrimaryEnvironmentTarget } from "../environments/primary";
import { useServerConfig } from "../rpc/serverState";
import { useWsConnectionStatus } from "../rpc/wsConnectionState";
import {
  buildStaleClientKey,
  resolveVersionMismatch,
  wasStaleClientReloadRequested,
  type VersionMismatch,
} from "../versionSkew";

export interface StaleClientBundleState {
  readonly mismatch: VersionMismatch;
  readonly staleKey: string;
}

function isReloadableClientBundle(): boolean {
  if (import.meta.env.DEV) {
    return false;
  }
  return readPrimaryEnvironmentTarget()?.source === "window-origin";
}

export function useStaleClientBundle(): StaleClientBundleState | null {
  const serverConfig = useServerConfig();
  const staleServerVersion = useWsConnectionStatus().staleServerVersion;
  const serverVersion = staleServerVersion ?? serverConfig?.environment.serverVersion ?? null;
  return useMemo(() => {
    if (serverVersion === null || !isReloadableClientBundle()) {
      return null;
    }
    const mismatch = resolveVersionMismatch(serverVersion);
    if (!mismatch) {
      return null;
    }
    const staleKey = buildStaleClientKey(mismatch);
    if (wasStaleClientReloadRequested(staleKey)) {
      return null;
    }
    return { mismatch, staleKey };
  }, [serverVersion]);
}
